import { Link } from "@inertiajs/react";
import { ArrowRight, Star, Trophy } from "lucide-react";
import Button from "../../../../components/Button/Button";
import carIcon from "../../../../assets/images/car_icon.svg";
import { Vehicle } from "../../../../types";
import "./styles.css";

export type TrophyCard = Vehicle & {
  award: string;
  image: string;
  path: string;
};

const TrophyVehicleCard = ({ vehicle }: { vehicle: TrophyCard }) => {
  const fullName = `${vehicle.brand.title} - ${vehicle.name}`;

  return (
    <div className="trophy-card">
      <img
        src={vehicle.image}
        alt={fullName}
        loading="lazy"
        className="trophy-card-image"
      />
      <span className="trophy-card-award">
        <Trophy className="w-3.5 h-3.5" />
        {vehicle.award}
      </span>
      <Link href={vehicle.path} className="trophy-card-name">
        {fullName}
      </Link>
      <div className="trophy-card-note">
        <Star className="w-4 h-4 primary-color fill-current self-center" />
        <span className="font-bold text-lg leading-5 primary-color">
          {vehicle.note?.toLocaleString("fr-FR") ?? "-"}
        </span>
        <span className="text-sm leading-4 -ml-1">/ 10</span>
        <span className="text-gray-500 font-light text-xs leading-4 ml-1">
          {vehicle.reviews_count} avis
        </span>
      </div>
      <Button styleClass="trophy-card-button" asChild>
        <Link href={vehicle.path}>
          <img src={carIcon} alt="" className="w-5" />
          Voir la fiche complète
        </Link>
      </Button>
    </div>
  );
};

type TrophiesSectionProps = {
  vehicles: TrophyCard[];
  showPalmaresLink?: boolean;
};

const TrophiesSection = ({
  vehicles,
  showPalmaresLink = true,
}: TrophiesSectionProps) => {
  if (vehicles.length === 0) return null;

  return (
    <section className="trophies-section">
      <div className="max-w-screen-xl mx-auto w-full">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <i className="rounded-full p-2 bg-primary inline-flex">
              <Trophy className="w-6 h-6 text-white" />
            </i>
            <span className="text-lg lg:text-2xl font-light uppercase tracking-wider">
              Trophées des véhicules électriques 2026
            </span>
          </div>
          {showPalmaresLink && (
            <Link
              href="/trophees-2026"
              className="text-sm font-medium text-white/90 hover:text-white hover:underline inline-flex items-center gap-1"
            >
              Voir le palmarès complet <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        <h2 className="text-2xl lg:text-3xl font-medium mt-5 max-w-[780px] text-balance">
          Les meilleures voitures électriques élues par les conducteurs
        </h2>
        <div className="mt-5 space-y-4 text-[15px] leading-6 text-white/90 max-w-[920px]">
          <p>
            Pour cette première édition, ce sont les conducteurs qui ont
            décidé&nbsp;: les Trophées récompensent les voitures électriques
            ayant obtenu les meilleures notes sur Review Car.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Renault 5 E-Tech</strong>&nbsp;: élue meilleure citadine
              électrique.
            </li>
            <li>
              <strong>Tesla Model Y</strong>&nbsp;: élue meilleure familiale
              électrique.
            </li>
            <li>
              <strong>Škoda Elroq</strong>&nbsp;: prix Top popularité, décerné
              au modèle dont le nombre d'avis a le plus progressé pendant la
              période de vote.
            </li>
          </ul>
          <p>
            Bravo aux trois lauréats, et merci à toutes celles et ceux qui ont
            partagé leur expérience&nbsp;!
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 text-foreground">
          {vehicles.map((vehicle) => (
            <TrophyVehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrophiesSection;
